import React, { useState, useEffect, useCallback } from 'react';
import { 
  Button
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { courseOfferService, courseService } from '../../services/api';
import { generateEqualInstallmentAmounts, calculateInstallmentsSum } from '../../helpers/subscriptionHelpers';

const CourseOfferForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    offerType: 'payPerUse', // 'payPerUse' or 'monthly'
    totalTickets: '',
    durationMonths: '',
    courseIds: [],
    hasInstallments: false,
    installmentCount: '3',
    installmentTotalAmount: ''
  });

  const [customizeDefaultInstallments, setCustomizeDefaultInstallments] = useState(false);
  const [defaultInstallments, setDefaultInstallments] = useState([]);

  const [selectedCourses, setSelectedCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const loadCourseOffer = useCallback(async () => {
    try {
      setLoading(true);
      const result = await courseOfferService.getById(id);
      
      if (result.success) {
        const offer = result.data;
        setFormData({
          name: offer.name,
          amount: offer.amount.toString(),
          offerType: offer.totalTickets ? 'payPerUse' : offer.durationMonths ? 'monthly' : 'freeDate',
          totalTickets: offer.totalTickets?.toString() || '',
          durationMonths: offer.durationMonths?.toString() || '',
          courseIds: offer.courseOfferCourses?.map(coc => coc.course.id) || [],
          hasInstallments: offer.hasInstallments || false,
          installmentCount: offer.installmentCount ? offer.installmentCount.toString() : '2',
          installmentTotalAmount: offer.installmentTotalAmount ? offer.installmentTotalAmount.toString() : ''
        });

        if (offer.hasInstallments && offer.defaultInstallments && offer.defaultInstallments.length > 0) {
          setCustomizeDefaultInstallments(true);
          const loaded = offer.defaultInstallments
            .map(di => ({
              installmentNumber: di.installmentNumber,
              amount: di.amount != null ? di.amount.toString() : '',
              dueDate: di.dueDate ? di.dueDate.split('T')[0] : ''
            }))
            .sort((a, b) => a.installmentNumber - b.installmentNumber);
          setDefaultInstallments(loaded);
        } else {
          setCustomizeDefaultInstallments(false);
          setDefaultInstallments([]);
        }
        
        // Imposta selectedCourses se presenti
        if (offer.courseOfferCourses && offer.courseOfferCourses.length > 0) {
          const courses = offer.courseOfferCourses.map(coc => ({
            value: coc.course.id,
            label: `${coc.course.name} (${coc.course.academicYear}/${coc.course.academicYear + 1})`
          }));
          setSelectedCourses(courses);
        }
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore nel caricamento dell\'abbonamento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadCourseOffer();
    }
  }, [isEdit, loadCourseOffer]);

  const loadCoursesAsync = useCallback(async (inputValue) => {
    try {
      const response = await courseService.getCoursesSummary(inputValue);
      if (response.success) {
        return (response.data || []).map(course => ({
          value: course.id,
          label: `${course.name} (${course.academicYear}/${course.academicYear + 1})`
        }));
      }
      return [];
    } catch (error) {
      return [];
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'hasInstallments' && !checked) {
      setCustomizeDefaultInstallments(false);
      setDefaultInstallments([]);
    }

    if (name === 'installmentCount' && customizeDefaultInstallments) {
      const newCount = parseInt(value, 10);
      if (!isNaN(newCount) && newCount >= 2 && newCount <= 12) {
        setDefaultInstallments(prev => {
          const total = parseFloat(formData.installmentTotalAmount) || parseFloat(formData.amount) || 0;
          const amounts = generateEqualInstallmentAmounts(newCount, total);
          const result = [];
          for (let i = 0; i < newCount; i++) {
            result.push({
              installmentNumber: i + 1,
              amount: (prev[i]?.amount != null && prev[i]?.amount !== '') ? prev[i].amount : amounts[i].toString(),
              dueDate: prev[i]?.dueDate || ''
            });
          }
          return result;
        });
      }
    }

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleToggleCustomizeInstallments = (enabled) => {
    setCustomizeDefaultInstallments(enabled);
    if (enabled && defaultInstallments.length === 0) {
      const count = parseInt(formData.installmentCount, 10) || 2;
      const total = parseFloat(formData.installmentTotalAmount) || parseFloat(formData.amount) || 0;
      const amounts = generateEqualInstallmentAmounts(count, total);
      setDefaultInstallments(amounts.map((amt, idx) => ({
        installmentNumber: idx + 1,
        amount: amt.toString(),
        dueDate: ''
      })));
    }
  };

  const handleInstallmentChange = (index, field, value) => {
    setDefaultInstallments(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    if (validationErrors.defaultInstallments) {
      setValidationErrors(prev => ({ ...prev, defaultInstallments: '' }));
    }
  };

  const effectiveTotalAmount = parseFloat(formData.installmentTotalAmount) || parseFloat(formData.amount) || 0;
  const installmentsSum = customizeDefaultInstallments
    ? calculateInstallmentsSum(defaultInstallments)
    : 0;
  const sumDiff = Math.round((installmentsSum - effectiveTotalAmount) * 100) / 100;
  const hasSumMismatch = Boolean(customizeDefaultInstallments && defaultInstallments.length > 0 && Math.abs(sumDiff) > 0.01);

  const handleAdaptPriceToSum = () => {
    setFormData(prev => ({
      ...prev,
      installmentTotalAmount: installmentsSum.toFixed(2)
    }));
  };

  const handleRecalculateEqualInstallments = () => {
    const count = defaultInstallments.length;
    if (count === 0) return;
    const amounts = generateEqualInstallmentAmounts(count, effectiveTotalAmount);
    setDefaultInstallments(prev => prev.map((inst, idx) => ({
      ...inst,
      amount: amounts[idx].toString()
    })));
  };

  const handleOfferTypeChange = (e) => {
    const offerType = e.target.value;
    setFormData(prev => ({
      ...prev,
      offerType,
      totalTickets: offerType === 'payPerUse' ? prev.totalTickets : '',
      durationMonths: offerType === 'monthly' ? prev.durationMonths : ''
    }));
  };

  const handleCourseChange = (selectedOptions) => {
    const selectedArray = selectedOptions || [];
    setSelectedCourses(selectedArray);
    setFormData(prev => ({
      ...prev,
      courseIds: selectedArray.map(option => option.value)
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Il nome è obbligatorio';
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount)) {
      errors.amount = 'Il prezzo è obbligatorio';
    } else if (amount < 0) {
      errors.amount = 'Il prezzo non può essere negativo';
    }

    if (formData.offerType === 'payPerUse') {
      if (!formData.totalTickets || parseInt(formData.totalTickets) <= 0) {
        errors.totalTickets = 'Il numero di ingressi deve essere maggiore di 0';
      }
    } else if (formData.offerType === 'monthly') {
      if (!formData.durationMonths || parseInt(formData.durationMonths) <= 0) {
        errors.durationMonths = 'La durata in mesi deve essere maggiore di 0';
      }
    }

    if (formData.hasInstallments) {
      const count = parseInt(formData.installmentCount);
      if (!formData.installmentCount || isNaN(count) || count < 2) {
        errors.installmentCount = 'Il numero di rate deve essere almeno 2';
      }
      const totalAmount = parseFloat(formData.installmentTotalAmount);
      if (formData.installmentTotalAmount && (isNaN(totalAmount) || totalAmount <= 0)) {
        errors.installmentTotalAmount = 'Il totale a rate deve essere maggiore di 0';
      }

      if (customizeDefaultInstallments) {
        if (!defaultInstallments || defaultInstallments.length !== count) {
          errors.defaultInstallments = `Configura tutte le ${count} rate previste`;
        } else {
          for (let i = 0; i < defaultInstallments.length; i++) {
            if (!defaultInstallments[i].dueDate) {
              errors.defaultInstallments = `Compila la data di scadenza per la Rata ${defaultInstallments[i].installmentNumber}`;
              break;
            }
            const amt = parseFloat(defaultInstallments[i].amount);
            if (isNaN(amt) || amt <= 0) {
              errors.defaultInstallments = `Importo non valido per la Rata ${defaultInstallments[i].installmentNumber}`;
              break;
            }
          }

          if (!errors.defaultInstallments) {
            for (let i = 1; i < defaultInstallments.length; i++) {
              if (new Date(defaultInstallments[i].dueDate) < new Date(defaultInstallments[i - 1].dueDate)) {
                errors.defaultInstallments = `La data della Rata ${defaultInstallments[i].installmentNumber} non può essere precedente alla Rata ${defaultInstallments[i - 1].installmentNumber}`;
                break;
              }
            }
          }

          if (!errors.defaultInstallments) {
            const sum = calculateInstallmentsSum(defaultInstallments);
            const target = parseFloat(formData.installmentTotalAmount) || parseFloat(formData.amount) || 0;
            if (Math.abs(sum - target) > 0.01) {
              errors.defaultInstallments = `La somma delle rate (€${sum.toFixed(2)}) deve coincidere con il totale a rate (€${target.toFixed(2)})`;
            }
          }
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Prepare data for API
      const apiData = {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        totalTickets: formData.offerType === 'payPerUse' ? parseInt(formData.totalTickets) : null,
        durationMonths: formData.offerType === 'monthly' ? parseInt(formData.durationMonths) : null,
        courseIds: formData.courseIds.length > 0 ? formData.courseIds : null,
        hasInstallments: formData.hasInstallments,
        installmentCount: formData.hasInstallments ? parseInt(formData.installmentCount) : null,
        installmentTotalAmount: formData.hasInstallments
          ? (parseFloat(formData.installmentTotalAmount) || parseFloat(formData.amount))
          : null,
        defaultInstallments: (formData.hasInstallments && customizeDefaultInstallments && defaultInstallments.length > 0)
          ? defaultInstallments.map(inst => ({
              installmentNumber: inst.installmentNumber,
              amount: parseFloat(inst.amount),
              dueDate: new Date(inst.dueDate + 'T00:00:00Z').toISOString()
            }))
          : null
      };

      const result = isEdit 
        ? await courseOfferService.update(id, apiData)
        : await courseOfferService.create(apiData);

      if (result.success) {
        navigate('/course-offers');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/course-offers');
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{isEdit ? 'Modifica abbonamento' : 'Nuovo abbonamento'}</h1>
            <Button variant="outline-secondary" onClick={handleCancel}>
              <i className="fas fa-arrow-left me-2"></i>
              Torna alla lista
            </Button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Nome Abbonamento <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Es. Pacchetto 10 ingressi"
                        disabled={loading}
                      />
                      {validationErrors.name && (
                        <div className="invalid-feedback">
                          {validationErrors.name}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="amount" className="form-label">
                        Prezzo (€) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={`form-control ${validationErrors.amount ? 'is-invalid' : ''}`}
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        disabled={loading}
                      />
                      {validationErrors.amount && (
                        <div className="invalid-feedback">
                          {validationErrors.amount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="offerType" className="form-label">
                        Tipo Abbonamento <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="offerType"
                        name="offerType"
                        value={formData.offerType}
                        onChange={handleOfferTypeChange}
                        disabled={loading}
                      >
                        <option value="payPerUse">Ingressi (Numero Ingressi)</option>
                        <option value="monthly">Mensile (Durata in Mesi)</option>
                        <option value="freeDate">Data Libera (Imposti le date quando iscrivi l'atleta)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {formData.offerType === 'freeDate' && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="alert alert-info py-2 mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Le date di inizio e fine non si inseriscono qui. Dopo aver salvato questo abbonamento, potrai scegliere liberamente le date <b>quando iscriverai un atleta a un gruppo</b>.
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="courseId" className="form-label">
                        Corso (Opzionale)
                      </label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={loadCoursesAsync}
                        onChange={handleCourseChange}
                        value={selectedCourses}
                        placeholder="Tutti i corsi (Abbonamento generale)"
                        noOptionsMessage={() => "Nessun corso trovato"}
                        loadingMessage={() => "Caricamento corsi..."}
                        isClearable
                        isMulti
                        isDisabled={loading}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: '38px'
                          })
                        }}
                      />
                      <div className="form-text">
                        Se non specifichi un corso, l'abbonamento sarà disponibile per tutti i corsi
                      </div>
                    </div>
                  </div>
                </div>

                {formData.offerType === 'payPerUse' && (
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="totalTickets" className="form-label">
                          Numero Ingressi <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          className={`form-control ${validationErrors.totalTickets ? 'is-invalid' : ''}`}
                          id="totalTickets"
                          name="totalTickets"
                          value={formData.totalTickets}
                          onChange={handleInputChange}
                          placeholder="Es. 10"
                          disabled={loading}
                        />
                        {validationErrors.totalTickets && (
                          <div className="invalid-feedback">
                            {validationErrors.totalTickets}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {formData.offerType === 'monthly' && (
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="durationMonths" className="form-label">
                          Durata (Mesi) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          className={`form-control ${validationErrors.durationMonths ? 'is-invalid' : ''}`}
                          id="durationMonths"
                          name="durationMonths"
                          value={formData.durationMonths}
                          onChange={handleInputChange}
                          placeholder="Es. 3"
                          disabled={loading}
                        />
                        {validationErrors.durationMonths && (
                          <div className="invalid-feedback">
                            {validationErrors.durationMonths}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sezione Rateizzazione */}
                <div className="card bg-light mb-3 border-0">
                  <div className="card-body">
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="hasInstallments"
                        name="hasInstallments"
                        checked={formData.hasInstallments}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <label className="form-check-label fw-bold" htmlFor="hasInstallments">
                        <i className="fas fa-calendar-alt me-2 text-primary"></i>
                        Abilita opzione di pagamento a rate
                      </label>
                      <div className="form-text">
                        Consente all'admin di rateizzare l'iscrizione per gli atleti che scelgono questa offerta.
                      </div>
                    </div>

                    {formData.hasInstallments && (
                      <>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label htmlFor="installmentCount" className="form-label">
                              Numero rate predefinito <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              min="2"
                              max="12"
                              className={`form-control ${validationErrors.installmentCount ? 'is-invalid' : ''}`}
                              id="installmentCount"
                              name="installmentCount"
                              value={formData.installmentCount}
                              onChange={handleInputChange}
                              placeholder="Es. 2 o 3"
                              disabled={loading}
                            />
                            {validationErrors.installmentCount && (
                              <div className="invalid-feedback">
                                {validationErrors.installmentCount}
                              </div>
                            )}
                            <div className="form-text">
                              {customizeDefaultInstallments
                                ? "Numero di rate da configurare nella tabella sottostante."
                                : "Le scadenze verranno calcolate suddividendo uniformemente la durata dell'offerta."}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="installmentTotalAmount" className="form-label">
                              Prezzo totale a rate (€)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className={`form-control ${validationErrors.installmentTotalAmount ? 'is-invalid' : ''}`}
                              id="installmentTotalAmount"
                              name="installmentTotalAmount"
                              value={formData.installmentTotalAmount}
                              onChange={handleInputChange}
                              placeholder={formData.amount ? `Default: ${formData.amount}` : 'Es. 330.00'}
                              disabled={loading}
                            />
                            {validationErrors.installmentTotalAmount && (
                              <div className="invalid-feedback">
                                {validationErrors.installmentTotalAmount}
                              </div>
                            )}
                            <div className="form-text">
                              Prezzo totale applicato se pagato a rate (se vuoto, usa il prezzo standard).
                            </div>
                          </div>
                        </div>

                        {/* Sezione Rate Predefinite */}
                        <div className="mt-4 pt-3 border-top">
                          <div className="form-check form-switch mb-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="customizeDefaultInstallments"
                              checked={customizeDefaultInstallments}
                              onChange={(e) => handleToggleCustomizeInstallments(e.target.checked)}
                              disabled={loading}
                            />
                            <label className="form-check-label fw-bold" htmlFor="customizeDefaultInstallments">
                              <i className="fas fa-calendar-check me-2 text-info"></i>
                              Imposta scadenze e importi di default per ciascuna rata
                            </label>
                            <div className="form-text">
                              Definisci le date di scadenza di calendario e gli importi che verranno proposti di default quando un atleta sceglie questa offerta.
                            </div>
                          </div>

                          {customizeDefaultInstallments && (
                            <div className="card border-info bg-white mb-2">
                              <div className="card-header bg-info bg-opacity-10 d-flex justify-content-between align-items-center py-2">
                                <div className="fw-bold text-dark small">
                                  <i className="fas fa-coins me-2 text-primary"></i>
                                  Piano Rate Predefinito ({defaultInstallments.length} rate)
                                </div>
                                <div className="small">
                                  Somma rate: <strong>€{installmentsSum.toFixed(2)}</strong> / Totale: <strong>€{effectiveTotalAmount.toFixed(2)}</strong>
                                </div>
                              </div>
                              <div className="card-body p-3">
                                {hasSumMismatch && (
                                  <div className="alert alert-warning py-2 px-3 mb-3 small d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                                    <div>
                                      <i className="fas fa-exclamation-triangle me-2"></i>
                                      <strong>Attenzione:</strong> La somma delle rate (€{installmentsSum.toFixed(2)}) non corrisponde al totale a rate (€{effectiveTotalAmount.toFixed(2)}).
                                    </div>
                                    <div className="d-flex gap-2">
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={handleAdaptPriceToSum}
                                        disabled={loading}
                                      >
                                        Adatta totale a somma rate
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={handleRecalculateEqualInstallments}
                                        disabled={loading}
                                      >
                                        Ricalcola rate uguali
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {validationErrors.defaultInstallments && (
                                  <div className="alert alert-danger py-2 px-3 mb-3 small">
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {validationErrors.defaultInstallments}
                                  </div>
                                )}

                                <div className="table-responsive">
                                  <table className="table table-sm align-middle mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: '15%' }}>Rata</th>
                                        <th style={{ width: '45%' }}>Data Scadenza predefinita <span className="text-danger">*</span></th>
                                        <th style={{ width: '40%' }}>Importo (€) <span className="text-danger">*</span></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {defaultInstallments.map((inst, index) => (
                                        <tr key={index}>
                                          <td className="fw-bold">Rata {inst.installmentNumber}</td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={inst.dueDate}
                                              onChange={(e) => handleInstallmentChange(index, 'dueDate', e.target.value)}
                                              disabled={loading}
                                              required
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              min="0.01"
                                              className="form-control form-control-sm"
                                              value={inst.amount}
                                              onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                                              disabled={loading}
                                              required
                                            />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <hr />
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Annulla
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {isEdit ? 'Aggiornamento...' : 'Creazione...'}
                          </>
                        ) : (
                          <>
                            <i className={`bi ${isEdit ? 'bi-check-circle' : 'bi-plus-circle'} me-2`}></i>
                            {isEdit ? 'Aggiorna Abbonamento' : 'Crea Abbonamento'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseOfferForm;